import React, { useCallback, useMemo, useRef, useState } from 'react';
import { uniqueId } from 'lodash';
import styled, { useTheme } from 'styled-components';
import EventDayComponent from './EventDay.component';
import { Notification } from '../../redux/Notification/NotificationType';
import CellComponent from '../Cell.component';
import { isSameDate } from '../../services/date.service';
import DatePickerWeekendComponent from './EventsPickerWeekend.component';
import IconButtonComponent from '../IconButton.component';
import PortalComponent from '../Portal.component';
import { Transition } from 'react-transition-group';
import EventComponent from './Event.component';
import MoreIcon from '../Icons/More.icon';
import { WEEKDAYS } from '../../constants/date.contant';
import { useTranslation } from 'react-i18next';

export interface EventsPickerProps extends React.AllHTMLAttributes<HTMLElement> {
  selectedDate: Date;
  rangeDates: {
    currentMonth: boolean,
    date: Date,
  }[];
  notifications: Notification[];
  onUpdate: Function;
  onRead: (event: Notification) => void;
}

const StyledDatePickerDays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  max-height: calc(100vh - 44px - 121px);
  overflow: auto;
`;
const StyledDatePicker = styled.div``;
const StyledTitle = styled.span<{
  weight?: string;
  level?: string;
}>`
  display: block;
  margin: 0;
`;
const StyledDayPopup = styled.div`
  width: 500px;
  height: 350px;
  border-radius: 10px;
  padding: 10px 20px;
  background-color: ${({ theme }) => theme.colors.NEUTRAL.N0};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 1px solid ${({theme}) => theme.colors.BORDER};
  box-shadow: 0 3px 20px rgba(0, 0, 0, 0.15);

  ${StyledTitle} {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 6px 0;
    width: 40px;
    height: 20px;
  }
  
  .event-day {
    height: 100%;
    width: 100%;
    border: none;

    .day-name {
      width: 40px;
      height: 40px;
      margin: 0;
      font-size: 16px;
    }
    
    .event-list {
      height: calc(100% - 52px);
      margin-top: 10px;
      overflow: auto;
      scrollbar-width: thin;
      
      .event-wrapper {
        position: initial;
        flex-grow: initial;
        min-height: 36px;
        
        .event-badge {
          height: 28px;
        }
      }
    }
  }
`;
const StyledMoreButton = styled.div`
  margin-top: 4px;
`;

const MORE_ICON_SIZE = 12;

const EventsPickerComponent = ({ rangeDates, notifications, selectedDate, onUpdate, onRead }: EventsPickerProps) => {
  const { t } = useTranslation();
  const theme = useTheme() as any;
  const eventRef = useRef(null);
  const [isMoreDropdown, setMoreDropdown] = useState(false);
  const [selectedDay, setSelectedDay] = useState(false);
  
  const searchEventsByDate = (date: Date) =>
    notifications
      .filter(notification => isSameDate(date, new Date(notification?.sendAt)))
      .sort((a, b) => a.sendAt - b.sendAt);
  
  const renderNotifications = useCallback(
    (day: any, dayNotifications: Notification[], renderAll: boolean = false) => {
      const countNotifications = dayNotifications.length;
      if (countNotifications > 5 && !renderAll) {
        const displayNotifications = dayNotifications.slice(0, 5);
        const hideNotificationsCount = dayNotifications.length - displayNotifications.length;
        
        return (
          <React.Fragment>
            {displayNotifications.map((notification: Notification) => {
              const key = uniqueId(`event-${notification.type}-`);
              return (
                <EventComponent
                  event={notification}
                  onUpdate={onUpdate}
                  onRead={onRead}
                  key={key}
                />);
            })}
            <CellComponent stretched key={uniqueId(`event-more-`)}>
              <StyledMoreButton>
                <IconButtonComponent
                  getRef={eventRef}
                  width={'100%'}
                  height={'24px'}
                  badge={hideNotificationsCount}
                  badgeAppearance="gray"
                  onClick={() => {
                    const dayName = WEEKDAYS[day.date.getDay() - 1];
                    const selected =
                      <StyledDayPopup>
                        <StyledTitle>{t(`weekdays.${dayName}`)}</StyledTitle>
                        <EventDayComponent
                          key={day.date.toDateString()}
                          isCurrentMonth={day.currentMonth}
                          date={day.date}
                          selectDate={selectedDate}
                          isEmpty={!dayNotifications || dayNotifications.length === 0}
                        >
                          {renderNotifications(day.date, dayNotifications, true)}
                        </EventDayComponent>
                      </StyledDayPopup>;
                    // @ts-ignore
                    setSelectedDay(selected);
                    setMoreDropdown(true);
                  }}
                  mode="default"
                >
                  <MoreIcon width={MORE_ICON_SIZE} />
                </IconButtonComponent>
              </StyledMoreButton>
            </CellComponent>
          </React.Fragment>
        );
      }
      
      return (
        <>
          {dayNotifications.map((notification: Notification) => {
            const key = uniqueId(`event-${notification.type}-`);
            return (
              <EventComponent
                onRead={onRead}
                event={notification}
                onUpdate={onUpdate}
                key={key}
                showType={renderAll}
              />
            );
          })}
        </>
      );
    },
    [],
  );
  
  const renderDays = useMemo(() => {
    return rangeDates.map(day => {
      // @ts-ignore
      const dayNotifications = searchEventsByDate(day.date);
      return (
        <EventDayComponent
          key={day.date.toDateString()}
          isCurrentMonth={day.currentMonth}
          date={day.date}
          selectDate={selectedDate}
          isEmpty={!dayNotifications || dayNotifications.length === 0}
        >
          {renderNotifications(day, dayNotifications)}
        </EventDayComponent>
      );
    });
  }, [selectedDate, rangeDates]);
  
  return (
    <React.Fragment>
      <StyledDatePicker>
        <DatePickerWeekendComponent />
        <StyledDatePickerDays>{renderDays}</StyledDatePickerDays>
      </StyledDatePicker>
      <Transition
        in={isMoreDropdown}
        mountOnEnter={true}
        unmountOnExit={true}
        timeout={{
          exit: 300,
        }}
      >
        {state => (
          <PortalComponent
            withOverlay={false}
            onClose={() => setMoreDropdown(false)}
            position="center"
            modalStyle={{
              ...theme.animations.defaultState,
              ...theme.animations.defaultSteps[state],
            }}
          >
            {selectedDay}
          </PortalComponent>
        )}
      </Transition>
    </React.Fragment>
  );
};

export default EventsPickerComponent;
